import {
  AUDIT_CLIENT_PROJECT_TYPE,
  AUDIT_PROJECT_TYPE,
  CORPORATE_FINANCE_PROJECT_TYPE,
  FAS_PROJECT_TYPE,
} from "@/core/lib/constants";
import type {
  AuditClientParameters,
  AuditParameters,
  CorporateFinanceParameters,
  FasParameters,
  Project,
  UserRead,
} from "@/openapi";

export type UserLike = Pick<
  UserRead,
  | "is_superuser"
  | "is_helpdesk_agent"
  | "is_org_admin"
  | "org_roles"
  | "project_type_memberships"
>;

export const isSuperuser = (user: UserLike | null | undefined): boolean =>
  user?.is_superuser === true;

export const isOrgOwner = (user: UserLike | null | undefined): boolean =>
  user?.org_roles?.includes("org_owner") ?? false;

export const isOrgAdmin = (user: UserLike | null | undefined): boolean =>
  user?.is_org_admin === true;

export const isOrgMember = (user: UserLike | null | undefined): boolean =>
  user?.org_roles?.includes("org_member") ?? false;

export const isHelpdeskAgent = (user: UserLike | null | undefined): boolean =>
  user?.is_helpdesk_agent === true;

const findMembership = (
  user: UserLike | null | undefined,
  projectType: string,
) =>
  user?.project_type_memberships?.find((m) => m.project_type === projectType);

export const isProjectTypeAdmin = (
  user: UserLike | null | undefined,
  projectType: string,
): boolean => findMembership(user, projectType)?.is_admin === true;

export const isProjectTypeMember = (
  user: UserLike | null | undefined,
  projectType: string,
): boolean => {
  const m = findMembership(user, projectType);
  return m?.is_admin === true || m?.is_member === true;
};

export const isAnyProjectAdmin = (user: UserLike | null | undefined): boolean =>
  user?.project_type_memberships?.some((m) => m.is_admin) ?? false;

export const canManageOrg = (user: UserLike | null | undefined): boolean =>
  isSuperuser(user) || isOrgAdmin(user);

export const canManageHelpCenter = (
  user: UserLike | null | undefined,
): boolean => isSuperuser(user) || isHelpdeskAgent(user);

export const isAuditAdmin = (user: UserLike | null | undefined): boolean =>
  isProjectTypeAdmin(user, AUDIT_PROJECT_TYPE);

export const isAuditMember = (user: UserLike | null | undefined): boolean =>
  isProjectTypeMember(user, AUDIT_PROJECT_TYPE);

export const auditParams = (
  client: Pick<AuditProject, "parameters">,
): AuditParameters => client.parameters;

export const corporateFinanceParams = (
  client: Pick<CorporateFinanceProject, "parameters">,
): CorporateFinanceParameters => client.parameters;

// sorts and year lists treat a dossier with no booking year as oldest
export const auditBookingYear = (
  dossier: Pick<AuditProject, "parameters">,
): number => dossier.parameters.booking_year;

/** A project whose `parameters` blob is known from its `project_type`. */
type ProjectOf<Type extends Project["project_type"], Parameters> = Omit<
  Project,
  "project_type" | "parameters"
> & {
  project_type: Type;
  parameters: Parameters;
};

export type AuditProject = ProjectOf<
  typeof AUDIT_PROJECT_TYPE,
  AuditParameters
>;
export type AuditClientProject = ProjectOf<
  typeof AUDIT_CLIENT_PROJECT_TYPE,
  AuditClientParameters
>;
export type CorporateFinanceProject = ProjectOf<
  typeof CORPORATE_FINANCE_PROJECT_TYPE,
  CorporateFinanceParameters
>;
export type FasProject = ProjectOf<typeof FAS_PROJECT_TYPE, FasParameters>;

/** What an audit client tree contains: client entity nodes and the per-year
 *  dossiers beneath them. The backend's `allowed_parent_types` admits nothing
 *  else. */
export type AuditNode = AuditProject | AuditClientProject;

export const isAuditProject = (project: Project): project is AuditProject =>
  project.project_type === AUDIT_PROJECT_TYPE;

export const isAuditClientProject = (
  project: Project,
): project is AuditClientProject =>
  project.project_type === AUDIT_CLIENT_PROJECT_TYPE;

export const isCorporateFinanceProject = (
  project: Project,
): project is CorporateFinanceProject =>
  project.project_type === CORPORATE_FINANCE_PROJECT_TYPE;

export const isFasProject = (project: Project): project is FasProject =>
  project.project_type === FAS_PROJECT_TYPE;
